import type { ComputedRef, InjectionKey } from "vue";
import type { RouteLocationRaw } from "vue-router";

/**
 * The semantics of the list body. `ul` and `ol` render their rows as `<li>`. `dl` renders a description list: each
 * row is a `<div>` with its title in a `<dt>` and its trailing content in a `<dd>`. `table` keeps the elements but
 * adds the ARIA table roles: the column header is a row of column headers and every row a row of cells.
 */
export type VListCardTag = "div" | "ul" | "ol" | "dl" | "table";

/** Attributes the card puts on its list body; bind them on your own body element in the `body` slot. */
export interface VListCardBodyAttrs {
  "class": unknown
  "style": Record<string, string> | undefined
  "role": string | undefined
  "aria-label": string | undefined
  "aria-labelledby": string | undefined
  "aria-describedby": string | undefined
  "aria-invalid": "true" | undefined
  "aria-busy": true | undefined
  "data-testid": string
}

/** Props of the `body` slot. */
export interface VListCardBodySlotProps {
  /** The element the body must be, such as `ul`: pass it as the `tag` of a VueDraggable. */
  tag: string
  /** Bind with `v-bind` on the body element: the dividers, scroll box, roles and names. */
  attrs: VListCardBodyAttrs
}

/** Ids a row passes to its slots, to name or describe controls by the row's texts. */
export interface VListCardRowSlotProps {
  /** Id of the title element. */
  titleId: string
  /** Id of the subtitle element, while the row has a subtitle. */
  subtitleId: string | undefined
  /** The row's `controlId`, when set. */
  controlId: string | undefined
}

export interface VListCardProps {
  /** Element of the list body. Use `ul` for a set of items, `ol` for ordered steps. */
  as?: VListCardTag
  /** Label on the left of the header, such as "12 locaties". Also names the list for screen readers. */
  title?: string
  /** Value on the right of the header, such as "3 met toegang", in the primary colour. */
  count?: string | number | null
  /**
   * Tailwind grid template classes shared by the `columns` header and every row, such as
   * `grid-cols-[minmax(0,1fr)_64px_64px]`. Turns each row into a grid: every element of the `leading` slot is a
   * cell, then the title block, then every element of the `trailing` slot.
   */
  columnsClass?: string
  /** Scrolls the rows inside this height (a number is px). The header, columns and footer stay visible. */
  maxHeight?: number | string
  /** Replaces the rows with skeleton rows. */
  loading?: boolean
  /** Number of skeleton rows while `loading`. */
  skeletonRows?: number
  /** The list has no rows at all: shows `emptyText` or the `empty` slot instead. */
  empty?: boolean
  /** Defaults to the generic "nothing here yet" text. */
  emptyText?: string
  /** A search matched no rows: shows `searchEmptyText` or the `search-empty` slot instead. */
  searchEmpty?: boolean
  /** Defaults to the generic "no results" text. */
  searchEmptyText?: string
  /** Shows a search field above the card, bound to `v-model:search`. Filtering stays with the caller. */
  searchable?: boolean
  /** Placeholder and accessible name of the search field. Defaults to the generic "search" text. */
  searchPlaceholder?: string
  /** Accessible name of the list when it has no `title`. */
  ariaLabel?: string
  /** Id of an element that names the list; wins over `title`. */
  ariaLabelledby?: string
  /** Id of an element that describes the list, such as an error about the whole selection. */
  ariaDescribedby?: string
  /**
   * The selection in the list has an error: the frame turns red and a named `div` body (a group) gets
   * `aria-invalid`. Point `ariaDescribedby` at the error message.
   */
  invalid?: boolean
  /**
   * Compact rows for a plain on/off list: every row is one line of about 36px with the subtitle inline after the
   * title. Rows inherit it; a row can override it with its own `dense`.
   */
  dense?: boolean
  testId?: string
}

export interface VListCardRowProps {
  title?: string
  /**
   * Secondary line under the title, for data only (a name, a count, a date). It also describes the row's control (see
   * the `leading` slot props). Explanations go in `help`.
   */
  subtitle?: string
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
  /** Element of the row. Defaults to `li` inside a `ul` or `ol` card, otherwise `div`. */
  as?: "div" | "li"
  /**
   * Id of the control in the row (a checkbox, radio or switch). The title becomes its `<label>`, stretched over
   * the whole row, so a click anywhere on the row toggles the control.
   */
  controlId?: string
  /** Makes the whole row a button: the title becomes a button stretched over the row and the row emits `click`. */
  clickable?: boolean
  /** Makes the whole row a link to this URL. */
  href?: string
  /** Makes the whole row a router link to this location. */
  to?: RouteLocationRaw
  /** Greys the row out and blocks the stretched label, button or link. */
  disabled?: boolean
  /** Tints the row as the chosen one. */
  selected?: boolean
  /**
   * Colour of the title: `muted` for an unavailable row, `danger` for a destructive or failed one, `active` for the
   * current one (such as the running step).
   */
  tone?: "default" | "muted" | "danger" | "active"
  /**
   * One compact line of about 36px: the subtitle sits inline after the title instead of under it. Defaults to the
   * card's `dense`.
   */
  dense?: boolean
  /** Lets the title and subtitle wrap onto more lines instead of cutting them off. */
  wrap?: boolean
  /**
   * Below the `sm` breakpoint the `trailing` content drops under the title block. `full` also stretches the
   * trailing controls to the full width there. Not used in a column grid.
   */
  stack?: boolean | "full"
  /** Vertical alignment of the leading content, the title block and the trailing content. `start` aligns them to the top. */
  align?: "center" | "start"
  /** A group sub-header between rows, such as an account or template name. */
  subheader?: boolean
  /** Shows a toggle button that opens the `expanded` slot under the row. */
  expandable?: boolean
  /** Accessible name of the expand toggle. Defaults to the generic "show details" and "hide details" texts. */
  expandLabel?: string
  testId?: string
}

export interface VListCardContext {
  tag: ComputedRef<VListCardTag>
  columnsClass: ComputedRef<string | undefined>
  dense: ComputedRef<boolean>
}

export const LIST_CARD_KEY: InjectionKey<VListCardContext> = Symbol("listCard");
